import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* ─── Share Link Modal ──────────────────────────────────────────────────── */
function ShareModal({ company, onClose, authFetch, showToast }) {
  const link = `${window.location.origin}/assess/${company.id}`;
  const [emails, setEmails]   = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(0);

  const copy = () => {
    navigator.clipboard.writeText(link);
    showToast('Assessment link copied!', 'info');
  };

  const send = async () => {
    const list = emails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e.includes('@'));
    if (!list.length) { showToast('Enter at least one valid email', 'error'); return; }
    if (list.length > 50) { showToast('Max 50 emails at once', 'error'); return; }
    setSending(true);
    const res  = await authFetch(`/api/companies/${company.id}/send-reminder`, {
      method: 'POST', body: JSON.stringify({ emails: list }),
    });
    const d = await res.json();
    if (res.ok) { setSent(d.sent); showToast(`${d.sent} reminder${d.sent !== 1 ? 's' : ''} sent!`); setEmails(''); }
    else showToast(d.error || 'Failed to send', 'error');
    setSending(false);
  };

  return (
    /* Portal renders directly into document.body — escapes overflow:auto clipping */
    createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}>

      {/* Modal box — scrolls internally if taller than viewport */}
      <div style={{
        background: 'var(--bg-surface)', borderRadius: 14,
        width: '100%', maxWidth: 500,
        maxHeight: 'calc(100vh - 32px)',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
      }}>

        {/* Header — always visible, never scrolls away */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '18px 22px', borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Share Assessment</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{company.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-3)', lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>

          {/* Link */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Assessment Link</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{
                flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '9px 12px', fontFamily: 'var(--ff-mono)', fontSize: 11,
                color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{link}</div>
              <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={copy}>📋 Copy</button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 6 }}>
              Anyone with this link can complete the assessment. No account required.
            </div>
          </div>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontSize: 11, color: 'var(--text-4)', fontWeight: 600 }}>OR SEND VIA EMAIL</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          {/* Email input */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Email Addresses</div>
            <textarea
              style={{
                width: '100%', minHeight: 90, fontFamily: 'var(--ff-mono)', fontSize: 12,
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '10px 12px', color: 'var(--text)',
                resize: 'vertical', boxSizing: 'border-box', outline: 'none',
              }}
              placeholder={'rajesh@company.com\npriya@company.com\nsuresh@company.com'}
              value={emails}
              onChange={e => setEmails(e.target.value)}
            />
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>
              {emails.split(/[\n,;]+/).filter(e => e.trim().includes('@')).length} valid emails · One per line, or comma-separated · Max 50
            </div>
          </div>

          {sent > 0 && (
            <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
              ✓ {sent} reminder{sent !== 1 ? 's' : ''} sent
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={sending || !emails.trim()} onClick={send}>
              {sending ? 'Sending…' : '📧 Send Reminders'}
            </button>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>

          {/* Tip */}
          <div style={{ background: 'var(--bg-subtle)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
            💡 <strong style={{ color: 'var(--text-2)' }}>Best results:</strong> Send to 15–30 people — mix of leadership (5–8), managers (8–12), and individual contributors (5–10).
          </div>
        </div>
      </div>
    </div>
    , document.body)
  );
}

/* ─── Companies Page ─────────────────────────────────────────────────────── */
export default function Companies({ showToast, forceNew }) {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [companies,     setCompanies]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(forceNew || false);
  const [form,          setForm]          = useState({ name:'', industry:'', size:'', website:'' });
  const [saving,        setSaving]        = useState(false);
  const [shareCompany,  setShareCompany]  = useState(null);

  const load = () => {
    authFetch('/api/companies').then(r => r.json())
      .then(d => { setCompanies(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async e => {
    e.preventDefault(); setSaving(true);
    const res  = await authFetch('/api/companies', { method: 'POST', body: JSON.stringify(form) });
    const data = await res.json();
    if (res.ok) {
      showToast('Company created!');
      setShowForm(false);
      setForm({ name:'', industry:'', size:'', website:'' });
      load();
    } else showToast(data.error, 'error');
    setSaving(false);
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its assessments?`)) return;
    const res = await authFetch(`/api/companies/${id}`, { method: 'DELETE' });
    if (res.ok) { showToast('Company deleted'); load(); }
    else showToast('Delete failed', 'error');
  };

  const INDUSTRIES = ['Technology','Manufacturing','Retail','Financial Services','Healthcare','Education','Professional Services','Real Estate','FMCG','Other'];
  const SIZES      = ['1–10','11–50','51–200','201–500','500–1000','1000+'];

  return (
    <div>
      <style>{`
        .companies-table-wrap { display: block; }
        .companies-mobile-cards { display: none; }
        @media (max-width: 640px) {
          .companies-table-wrap { display: none !important; }
          .companies-mobile-cards { display: flex !important; }
        }
      `}</style>
      {/* Share Modal */}
      {shareCompany && (
        <ShareModal
          company={shareCompany}
          onClose={() => setShareCompany(null)}
          authFetch={authFetch}
          showToast={showToast}
        />
      )}

      <div className="page-header">
        <div>
          <div className="page-title">Companies</div>
          <div className="page-sub">Manage your organizational diagnostic workspaces</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ New Company</button>
      </div>

      {/* New company form */}
      {showForm && (
        <div className="card card-accent" style={{ marginBottom: 24 }}>
          <div className="card-title">Create Company Workspace</div>
          <form onSubmit={submit}>
            <div className="g2" style={{ gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Company Name *</label>
                <input className="form-input" placeholder="Acme Manufacturing Pvt Ltd"
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Website</label>
                <input className="form-input" placeholder="https://acme.com"
                  value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Industry</label>
                <select className="form-select" value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })}>
                  <option value="">Select industry</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Company Size</label>
                <select className="form-select" value={form.size} onChange={e => setForm({ ...form, size: e.target.value })}>
                  <option value="">Select size</option>
                  {SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Company'}</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Company list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="loader" style={{ margin: '0 auto' }} /></div>
      ) : companies.length === 0 && !showForm ? (
        <div className="card empty-state">
          <div className="empty-icon">🏢</div>
          <div className="empty-title">No companies yet</div>
          <div className="empty-sub">Create your first company workspace to begin the organizational diagnostic</div>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Create Company</button>
        </div>
      ) : (
        <>
        <div className="companies-table-wrap" style={{ padding: 0, overflow: 'hidden' }} >
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table">
            <thead>
              <tr><th>Company</th><th>Industry</th><th>Size</th><th>Responses</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2, fontFamily: 'var(--ff-m)' }}>{c.id.slice(0,8)}...</div>
                  </td>
                  <td>{c.industry || '—'}</td>
                  <td>{c.size || '—'}</td>
                  <td><span style={{ fontFamily: 'var(--ff-m)', color: 'var(--blue)', fontSize: 16, fontWeight: 500 }}>{c.response_count || 0}</span></td>
                  <td>
                    {(c.response_count || 0) >= 5
                      ? <span className="badge priority-strong" style={{ fontSize: 10 }}>● Ready</span>
                      : (c.response_count || 0) >= 1
                      ? <span className="badge priority-misaligned" style={{ fontSize: 10 }}>○ Collecting</span>
                      : <span className="badge" style={{ fontSize: 10, color: 'var(--text2)', borderColor: 'var(--border)' }}>○ No responses</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => setShareCompany(c)}>🔗 Share Link</button>
                      {(c.response_count || 0) > 0 && (
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/report', { state: { companyId: c.id } })}>View Report</button>
                      )}
                      <button className="btn btn-danger btn-sm" onClick={() => del(c.id, c.name)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>

        {/* Mobile cards — shown instead of table on small screens */}
        <div className="companies-mobile-cards" style={{ flexDirection: 'column', gap: 12 }}>
          {companies.map(c => (
            <div key={c.id} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{c.industry || '—'} · {c.size || '—'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'var(--ff-m)', color: 'var(--blue)', fontSize: 18, fontWeight: 700 }}>{c.response_count || 0}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-4)' }}>responses</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => setShareCompany(c)}>🔗 Share Link</button>
                {(c.response_count || 0) > 0 && (
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => navigate('/report', { state: { companyId: c.id } })}>📊 Report</button>
                )}
                <button className="btn btn-danger btn-sm" onClick={() => del(c.id, c.name)}>✕</button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
